/**
 * The inventory report workbench (batch 8 T14c, #3036): seven report views over the T14a read API. Exported ready to
 * mount; the Inventory tab, route and navigation that host it are T15's.
 *
 * Rules the screen keeps:
 *  - read only: nothing here writes, and no report runs until the user runs it (no auto-apply while typing);
 *  - the variant a user may read is decided by their permissions, and a request they may not make is never sent: a
 *    user without the financial right gets the units views and no money field exists in the page at all;
 *  - everything on the screen belongs to the signed-in owner (session, tenant, user and the report permissions):
 *    when any of them changes the body is replaced, so no filter, selection, result or cached answer of the
 *    previous owner is left, and a late answer of theirs has nowhere to land;
 *  - the previous answer may stay while a new page or filter loads, but only the same owner's, and it is marked
 *    stale so it is never read as the answer to the new filters;
 *  - totals are the server's whole-result figures; the portal sums nothing and rounds nothing.
 */
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, FormControlLabel, LinearProgress, MenuItem, Paper, Stack, Switch, Tab, Tabs, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  REPORT_KINDS,
  reportErrorKey,
  reportKeys,
  useInventoryReport,
  useReportCategories,
  useReportLookup,
  useReportsOwner,
  useReportWarehouses,
  type PriceKind,
  type ReportKind,
  type ReportMap,
  type ReportParams,
} from '@/hooks/useInventoryReports';
import { ProfitabilityView, SupplierPerformanceView } from './MarginViews';
import { PriceHistoryView } from './PriceHistoryView';
import { ReportPager } from './Pager';
import { ReportFilterForm, FilterErrorsAlert } from './ReportFilterForm';
import { ReportDisplayContext, type ReportDisplay } from './ReportParts';
import { AbcView, DeadSlowView, TurnoverView } from './StockViews';
import { ValuationView } from './ValuationView';
import { PAGE_SIZES, buildParams, defaultFilters, withPage, type FieldErrors, type FilterState, type Picked } from './reportFilters';
import { useOwnerPartLabels } from './partNames';
import type { ReportsAccess } from './reportsSession';

/** Reports with no variant a user without the financial right may read. */
const FINANCIAL_ONLY: readonly ReportKind[] = ['profitability', 'valuation'];

export function InventoryReportsWorkbench() {
  const { t } = useTranslation('inventoryReports');
  const { access, owner } = useReportsOwner();
  if (!access.canRead) return <Alert severity="warning">{t('access.denied')}</Alert>;
  return <WorkbenchBody key={owner} access={access} />;
}

type PerKind<T> = Record<ReportKind, T>;
const perKind = <T,>(make: (kind: ReportKind) => T): PerKind<T> => Object.fromEntries(REPORT_KINDS.map((k) => [k, make(k)])) as PerKind<T>;

function WorkbenchBody({ access }: { access: ReportsAccess }) {
  const { t } = useTranslation('inventoryReports');
  const queryClient = useQueryClient();
  const { owner } = useReportsOwner();
  const [tab, setTab] = useState<ReportKind>('abc');
  const [drafts, setDrafts] = useState<PerKind<FilterState>>(() =>
    perKind((kind) => ({ ...defaultFilters(access), groupBy: kind === 'turnover' || kind === 'valuation' ? 'category' : 'part' })),
  );
  const [applied, setApplied] = useState<Partial<Record<ReportKind, ReportParams>>>({});
  const [offsets, setOffsets] = useState<PerKind<number>>(() => perKind(() => 0));
  const [errors, setErrors] = useState<PerKind<FieldErrors>>(() => perKind(() => ({})));
  const [dirty, setDirty] = useState<PerKind<boolean>>(() => perKind(() => false));
  const [limit, setLimit] = useState<number>(PAGE_SIZES[0]);
  const [precise, setPrecise] = useState(false);

  const draft = drafts[tab];
  const params = applied[tab];
  const denied = FINANCIAL_ONLY.includes(tab) && !access.canFinancial;

  const change = (patch: Partial<FilterState>) => {
    setDrafts((current) => ({ ...current, [tab]: { ...current[tab], ...patch } }));
    setDirty((current) => ({ ...current, [tab]: true }));
  };
  const run = () => {
    const built = buildParams(tab, draft);
    if (!built.ok) {
      setErrors((current) => ({ ...current, [tab]: built.errors }));
      return;
    }
    setErrors((current) => ({ ...current, [tab]: {} }));
    // A new filter always starts at the first page.
    setOffsets((current) => ({ ...current, [tab]: 0 }));
    setApplied((current) => ({ ...current, [tab]: built.params }));
    setDirty((current) => ({ ...current, [tab]: false }));
    // Running is asking: an earlier answer of this report is stale, so the same filters again read it again. The
    // answer on screen refetches only when the filters are the same (a changed filter is a new request anyway).
    const same = params !== undefined && JSON.stringify(params) === JSON.stringify(built.params);
    void queryClient.invalidateQueries({ queryKey: [...reportKeys.all, owner, 'report', tab], refetchType: same ? 'active' : 'none' });
  };

  const picks = useMemo(() => picksOf(draft), [draft]);

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5" component="h2">
          {t('title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(access.canFinancial ? 'intro.financial' : 'intro.unitsOnly')}
        </Typography>
      </Box>
      <Tabs value={tab} onChange={(_event, next: ReportKind) => { setTab(next); }} variant="scrollable" scrollButtons="auto" aria-label={t('tabsLabel')}>
        {REPORT_KINDS.map((kind) => (
          <Tab key={kind} value={kind} label={t(`tabs.${kind}`)} id={`report-tab-${kind}`} aria-controls="report-panel" />
        ))}
      </Tabs>
      <Paper variant="outlined" sx={{ p: 2 }} role="tabpanel" id="report-panel" aria-labelledby={`report-tab-${tab}`}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t(`descriptions.${tab}`)}
          </Typography>
          {denied ? (
            <Alert severity="info">{t('access.financialOnly')}</Alert>
          ) : (
            <>
              <ReportFilterForm kind={tab} state={draft} errors={errors[tab]} access={access} onChange={change} />
              <FilterErrorsAlert errors={errors[tab]} />
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button variant="contained" onClick={run}>
                  {params === undefined ? t('actions.run') : t('actions.rerun')}
                </Button>
                <TextField
                  select
                  size="small"
                  label={t('paging.rowsPerPage')}
                  value={limit}
                  onChange={(event) => {
                    setLimit(Number(event.target.value));
                    setOffsets(perKind(() => 0));
                  }}
                  sx={{ minWidth: 120 }}
                >
                  {PAGE_SIZES.map((size) => (
                    <MenuItem key={size} value={size}>{size}</MenuItem>
                  ))}
                </TextField>
                <FormControlLabel
                  control={<Switch checked={precise} onChange={(event) => { setPrecise(event.target.checked); }} />}
                  label={t('actions.precise')}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {t('money.netEur')}
              </Typography>
              {params === undefined ? (
                <Alert severity="info">{t('state.notRun')}</Alert>
              ) : (
                <>
                  {dirty[tab] && <Alert severity="warning">{t('state.filtersChanged')}</Alert>}
                  {tab === 'price-history' ? (
                    <PriceHistoryRun params={params} limit={limit} precise={precise} picks={picks} />
                  ) : (
                    <ReportRun
                      key={tab}
                      kind={tab}
                      params={params}
                      limit={limit}
                      offset={offsets[tab]}
                      precise={precise}
                      picks={picks}
                      onOffset={(offset) => { setOffsets((current) => ({ ...current, [tab]: offset })); }}
                      onLimit={(next) => {
                        setLimit(next);
                        setOffsets(perKind(() => 0));
                      }}
                    />
                  )}
                </>
              )}
            </>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}

/** Every named choice of the filters, so a chip or a group row can say its name. */
function picksOf(draft: FilterState): { parts: Picked[]; suppliers: Picked[] } {
  const parts = [...draft.parts, ...(draft.part === null ? [] : [draft.part])];
  return { parts, suppliers: draft.supplier === null ? [] : [draft.supplier] };
}

interface RunProps {
  limit: number;
  precise: boolean;
  picks: { parts: Picked[]; suppliers: Picked[] };
}

/** The display context of a result: names for ids from what THIS owner's report screen already loaded, never from a new bulk read. */
function useDisplay(precise: boolean, picks: RunProps['picks'], version: unknown): ReportDisplay {
  const { i18n } = useTranslation('inventoryReports');
  const { owner } = useReportsOwner();
  const partLabels = useOwnerPartLabels(owner);
  const categories = useReportCategories();
  const warehouses = useReportWarehouses();
  const suppliers = useReportLookup('supplier', '');
  return useMemo(() => {
    void version;
    const cached = new Map(partLabels);
    for (const p of picks.parts) cached.set(p.id, p.label);
    const categoryMap = new Map(categories.options.map((c) => [c.id, c.name]));
    const warehouseMap = new Map(warehouses.options.map((w) => [w.id, w.name]));
    const supplierMap = new Map(suppliers.options.map((s) => [s.id, s.label.replace(/ \([^)]*\)$/, '')]));
    for (const s of picks.suppliers) supplierMap.set(s.id, s.label.replace(/ \([^)]*\)$/, ''));
    return {
      language: i18n.language,
      precise,
      partName: (id) => cached.get(id) ?? null,
      categoryName: (id) => categoryMap.get(id) ?? null,
      warehouseName: (id) => warehouseMap.get(id) ?? null,
      supplierName: (id) => supplierMap.get(id) ?? null,
    };
  }, [i18n.language, precise, picks, version, partLabels, categories.options, warehouses.options, suppliers.options]);
}

function PriceHistoryRun({ params, limit, precise, picks }: RunProps & { params: ReportParams }) {
  const display = useDisplay(precise, picks, params);
  const { kind: kindText, ...base } = params;
  const kinds = (kindText ?? 'sell').split(',') as PriceKind[];
  return (
    <ReportDisplayContext.Provider value={display}>
      {/* A new request (filters or page size) is a new set of panels, each at its own first page. */}
      <PriceHistoryView key={`${JSON.stringify(params)}|${String(limit)}`} base={base} kinds={kinds} pageSize={limit} />
    </ReportDisplayContext.Provider>
  );
}

type PagedKind = Exclude<ReportKind, 'price-history'>;
type PagedReport = ReportMap[PagedKind];

function rowCount(kind: PagedKind, data: PagedReport): number {
  switch (kind) {
    case 'abc':
    case 'dead-and-slow':
    case 'profitability':
      return (data as ReportMap['abc']).rows.length;
    case 'turnover':
    case 'valuation':
      return (data as ReportMap['turnover']).groups.length;
    case 'supplier-performance':
      return (data as ReportMap['supplier-performance']).suppliers.length;
  }
}

function ReportRun({
  kind,
  params,
  limit,
  offset,
  precise,
  picks,
  onOffset,
  onLimit,
}: RunProps & { kind: PagedKind; params: ReportParams; offset: number; onOffset: (offset: number) => void; onLimit: (limit: number) => void }) {
  const { t } = useTranslation('inventoryReports');
  const query = useInventoryReport(kind, withPage(params, limit, offset));
  const display = useDisplay(precise, picks, query.data);
  const queryClient = useQueryClient();
  const { owner } = useReportsOwner();
  const data = query.data;
  // Jumping back from a page past the end means the result changed: read it again instead of trusting an older page.
  const jump = (next: number) => {
    if (data !== undefined && rowCount(kind, data) === 0 && data.pagination.total > 0) {
      void queryClient.invalidateQueries({ queryKey: [...reportKeys.all, owner, 'report', kind], refetchType: 'none' });
    }
    onOffset(next);
  };
  if (query.denied !== null) return <Alert severity="info">{t('access.financialOnly')}</Alert>;
  return (
    <ReportDisplayContext.Provider value={display}>
      <Stack spacing={2} aria-busy={query.isFetching} sx={{ opacity: query.isStale ? 0.6 : 1 }}>
        {query.isFetching && <LinearProgress aria-label={t('state.loading')} />}
        {query.isStale && <Alert severity="info">{t('state.stale')}</Alert>}
        {query.error !== null && query.error !== undefined && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={query.refetch}>
                {t('actions.retry')}
              </Button>
            }
          >
            {t(reportErrorKey(query.error))}
          </Alert>
        )}
        {data !== undefined && (
          <>
            {data.pagination.total === 0 && <Alert severity="info">{t('state.empty')}</Alert>}
            <ReportBody kind={kind} data={data} />
            <ReportPager pagination={data.pagination} rows={rowCount(kind, data)} onOffset={jump} onLimit={onLimit} disabled={query.isFetching} />
          </>
        )}
      </Stack>
    </ReportDisplayContext.Provider>
  );
}

function ReportBody({ kind, data }: { kind: PagedKind; data: PagedReport }) {
  switch (kind) {
    case 'abc':
      return <AbcView data={data as ReportMap['abc']} />;
    case 'turnover':
      return <TurnoverView data={data as ReportMap['turnover']} />;
    case 'dead-and-slow':
      return <DeadSlowView data={data as ReportMap['dead-and-slow']} />;
    case 'profitability':
      return <ProfitabilityView data={data as ReportMap['profitability']} />;
    case 'supplier-performance':
      return <SupplierPerformanceView data={data as ReportMap['supplier-performance']} />;
    case 'valuation':
      return <ValuationView data={data as ReportMap['valuation']} />;
  }
}
